import * as yup from "yup";
import moment from "moment";
import { useFormik } from "formik";
import { USER_PAYMENT_SYSTEMS } from "../../../consts/api";
import { uiNotification } from "../../../services/UINotificationService";
import { APPOINTMENT_TYPES, APPT_API_TIME_FORMAT } from "../Event.consts";
import {
  wrapFormDate,
  getCalendarStartDate,
  initFormDate,
} from "../Event.utils";
import { phoneUtil } from "../../../utilities/phone";
import {
  Maybe,
  cond,
  identity,
  pipe,
  positive,
  strDefault,
} from "../../../utilities/fp";
import { useAppTranslation } from "../../../i18n/useAppTranslation";

export const getCreateFormInitialDate = (params = {}) => {
  return initFormDate({
    date: params.date,
    boundaryDate: wrapFormDate(new Date()),
  });
};

const initAppointmentCreateForm = ({
  userPaymentSystem,
  params,
  clientById,
  userTimeFormat,
  isLeadMode,
  isDobRequired,
  selectedClearentCard,
}) => {
  return {
    isEditMode: false,

    isDobRequired,

    isLeadDobMissing: isLeadMode && !clientById.dateOfBirth,

    userPaymentSystem,

    type: APPOINTMENT_TYPES.inPerson,

    providerId: Maybe.of(params.providerId)
      .map(Number)
      .map((x) => pipe(x, cond(strDefault, [positive, identity])))
      .orElse("")
      .value(),

    clinicId: params.clinicId ? Number(params.clinicId) : "",

    services: [],

    isDoubleBooking: false,

    ignoreAdditionalProviderSchedule: false,

    isOutsideScheduledHours: false,

    isEnterCreditCardDetails: false,

    calendarStartDate: wrapFormDate(getCalendarStartDate(params.date)),

    date: getCreateFormInitialDate(params),

    timeStart: params.time
      ? moment(params.time, APPT_API_TIME_FORMAT).format(userTimeFormat)
      : "",

    clientId: clientById.id || "",

    clientName: clientById.fullName || "",

    clientEmail: clientById.email || "",

    clientPhone: clientById.phone || "",

    clientDob: clientById.dateOfBirth || "",

    notes: "",

    clearentZipCode: clientById.pinCode || "",

    clearentEmail: clientById.email || "",

    isAddNewCard: false,

    isPatientToBeCharged: false,

    isCancellationPolicyExcludeConfirmed: false,

    isConvertingWaitlist: false,

    selectedClearentCard: selectedClearentCard || "",

    additional_provider_id: null,
  };
};

const initAppointmentEditForm = ({
  userPaymentSystem,
  editEventData,
  params,
  userTimeFormat,
  isDobRequired,
  selectedClearentCard,
}) => {
  return {
    isEditMode: true,
    isDobRequired,
    userPaymentSystem,
    type: editEventData.type,
    providerId: editEventData.providerId,
    clinicId: params.clinicId
      ? Number(params.clinicId)
      : editEventData.clinicId,
    services: editEventData.services,
    isDoubleBooking: editEventData.isDoubleBooking,
    ignoreAdditionalProviderSchedule:
      editEventData.ignoreAdditionalProviderSchedule,
    isOutsideScheduledHours: editEventData.isOutsideScheduledHours,
    calendarStartDate: wrapFormDate(getCalendarStartDate(editEventData.date)),
    date: editEventData.date,
    timeStart: moment(
      params.time || editEventData.timeStart,
      APPT_API_TIME_FORMAT,
    ).format(userTimeFormat),
    clientId: editEventData.clientId,
    clientName: editEventData.clientName,
    clientEmail: editEventData.clientEmail,
    clientPhone: editEventData.clientPhone,
    clientDob: editEventData.clientDob || "",
    notes: editEventData.notes,
    clearentZipCode: editEventData.clientPinCode,
    clearentEmail: editEventData.clientEmail,
    isAddNewCard: false,
    isEnterCreditCardDetails: false,
    isPatientToBeCharged: false,
    isCancellationPolicyExcludeConfirmed: false,
    isConvertingWaitlist: false,
    selectedClearentCard: selectedClearentCard || "",
    additional_provider_id: editEventData.additional_provider_id || null,
  };
};

const initConvertWaitlistForm = ({ history }) => {
  let waitlistFormData = {};

  const {
    id,
    patient,
    appointment_notes,
    appointment_wait_list_clinic,
    appointment_wait_list_services,
  } = history.location.state;

  waitlistFormData = {
    type: appointment_wait_list_services?.every(
      (s) => s.serviceType === APPOINTMENT_TYPES.virtual,
    )
      ? APPOINTMENT_TYPES.virtual
      : APPOINTMENT_TYPES.inPerson,
    isConvertingWaitlist: true,
    clinicId: appointment_wait_list_clinic,

    clientName: patient.full_name,
    clientId: patient.id,
    clientEmail: patient.email,
    clearentEmail: patient.email,
    clientPhone: patient.phoneNumber,
    clientDob: patient.date_of_birth || "",
    notes: appointment_notes,

    services: appointment_wait_list_services.map((serviceDetails) => ({
      name: serviceDetails.name,
      id: serviceDetails.id,
      durationInMin: serviceDetails.duration,
      isFree: serviceDetails.isFree,
      price: serviceDetails.price,
      isSkipPaymentCheck: false,
      isDepositConfirmed: false,
    })),
    appointment_waitlist_id: id,
  };
  return waitlistFormData;
};

export const initAppointmentForm = ({
  userPaymentSystem,
  params,
  clientById,
  editEventData,
  history,
  userTimeFormat,
  isLeadMode,
  isDobRequired,
  selectedClearentCard,
}) => {
  if (
    history &&
    history.location &&
    history.location.state &&
    "appointment_wait_list_clinic" in history.location.state
  ) {
    return {
      ...initAppointmentCreateForm({
        userPaymentSystem,
        clientById,
        params,
        userTimeFormat,
        isLeadMode,
        isDobRequired,
        selectedClearentCard,
      }),
      ...initConvertWaitlistForm({ history }),
    };
  }
  if (editEventData) {
    return initAppointmentEditForm({
      userPaymentSystem,
      editEventData,
      params,
      userTimeFormat,
      isDobRequired,
      selectedClearentCard,
    });
  }
  return initAppointmentCreateForm({
    userPaymentSystem,
    clientById,
    params,
    userTimeFormat,
    isLeadMode,
    isDobRequired,
    selectedClearentCard,
  });
};

export function useFormAppointment(initialValues) {
  const { tCommon } = useAppTranslation.Common();

  const schema = yup.object({
    isEditMode: yup.bool(),

    isDobRequired: yup.bool(),

    userPaymentSystem: yup.string(),

    calendarStartDate: yup.string(),

    type: yup.string().required("Select Appointment Type"),

    providerId: yup.number().required("Select provider"),

    clinicId: yup.number().required("Select clinic"),

    services: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.number().required(),
          name: yup.string().required(),
          durationInMin: yup.number().required(),
          isDepositConfirmed: yup.bool().required(),
          isFree: yup.bool().required(),
          isSkipPaymentCheck: yup.bool().required(),
          price: yup.number(),
        }),
      )
      .min(1, "Select at least one service"),

    isDoubleBooking: yup.bool().required(),

    ignoreAdditionalProviderSchedule: yup.bool().required(),

    isOutsideScheduledHours: yup.bool().required(),

    isEnterCreditCardDetails: yup.bool().required(),

    date: yup.string().required("Select date"),

    timeStart: yup.string().required("Select start time"),

    clientId: yup.number().test({
      name: "clientIdRequired",
      message: tCommon("error.clientIdSearch"),
      test: function (value) {
        return !(!value && this.parent.clientName);
      },
    }),

    appointment_waitlist_id: yup.number(),

    clientName: yup.string().required(tCommon("error.clientSearch")),

    clientEmail: yup
      .string()
      .email(tCommon("error.clientEmailInvalid"))
      .test({
        message: tCommon("error.clientEmailRequired"),
        test: (value, context) => {
          const isPhoneRequired =
            context.parent.type === APPOINTMENT_TYPES.virtual;
          return !(isPhoneRequired && !value);
        },
      }),

    clientDob: yup.lazy((_, { context }) => {
      if (context.isDobRequired) {
        return yup.string().required("Provide date of birth");
      }
      return yup.string().nullable().notRequired();
    }),

    clientPhone: yup
      .string()
      .test({
        message: "Please provide phone number",
        test: (value, context) => {
          const isPhoneRequired =
            context.parent.type === APPOINTMENT_TYPES.virtual;
          return !(isPhoneRequired && !value);
        },
      })
      .test({
        message: "Provide valid phone number",
        test: (value) => {
          if (!value) {
            return true;
          }
          return phoneUtil.isPossible(phoneUtil.compose(value));
        },
      }),

    notes: yup.string().nullable(),

    clearentZipCode: yup.string().test({
      name: "clearentZipCodeRequired",
      message: "Provide Zip Code",
      test: function (value) {
        const paymentSystem = this.parent.userPaymentSystem;
        const isEnterCreditCardDetails = this.parent.isEnterCreditCardDetails;
        if (
          paymentSystem === USER_PAYMENT_SYSTEMS.clearent &&
          isEnterCreditCardDetails &&
          !value
        ) {
          return false;
        }
        return true;
      },
    }),

    clearentEmail: yup
      .string()
      .test({
        name: "clearentEmailRequired",
        message: "Provide Email for credit card data",
        test: function (value) {
          const paymentSystem = this.parent.userPaymentSystem;
          const isEnterCreditCardDetails = this.parent.isEnterCreditCardDetails;
          if (
            paymentSystem === USER_PAYMENT_SYSTEMS.clearent &&
            isEnterCreditCardDetails &&
            !value
          ) {
            return false;
          }
          return true;
        },
      })
      .email("Provide valid email for credit card data"),

    isAddNewCard: yup.bool().required(""),

    isPatientToBeCharged: yup.bool().required(""),

    isCancellationPolicyExcludeConfirmed: yup.bool(),

    isConvertingWaitlist: yup.bool(),

    selectedClearentCard: yup.string().nullable().notRequired(),
  });

  const { values, errors, setFieldValue, setFieldError, validateForm } =
    useFormik({
      initialValues,
      validationSchema: schema,
      enableReinitialize: true,
      validateOnChange: false,
      onSubmit: () => {},
    });

  const submit = (submitter, partialValues = {}) => {
    const nextValues = { ...values, ...partialValues };
    validateForm(nextValues).then((errors) => {
      if (Object.keys(errors).length === 0) {
        submitter(nextValues, { setFieldError, setFieldValue });
      } else {
        Object.values(errors).forEach((message) => {
          if (message) {
            uiNotification.error(message);
          }
        });
      }
    });
  };

  const hasError = (field) => {
    return Object.keys(errors).includes(field);
  };

  return {
    form: values,
    setFormValue: setFieldValue,
    submit,
    hasError,
  };
}
